import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  runInInjectionContext,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  RecordData,
  RecordTableColumnParams,
  RecordTotalData,
  RecordUpdateParams
} from "../../types/records-table.types";
import {RecordTableAction} from "../../types/record-table-action.types";
import {Pageable} from "../../types/pagable.types";
import {ResourceParams} from "../../models/resource/resources";
import {defaultResourceParams} from "../../models/resource/default-resource-params";
import {UtilityService} from "../../services/utility.service";
import {ResizeService} from "../../services/resize.service";
import {TelegramNotificationsStateService} from "../../services/telegram-notifications-state.service";
import {
  TelegramNotificationsPollingService
} from "../records-table-container/services/telegram-notifications-polling.service";
import {
  availableTableActions,
  reorderColumnsAccordingToOrderProperty
} from "./configs/records-table-config";
import {SortOrder} from "../../types/utility.types";
import {Subscription} from "rxjs";
import {ColumnParamsStateService} from "../../services/table-services/column-params-state.service";
import {ColumnResizeService} from "../../services/table-services/column-resize.service";
import {RecordsTableViewComponentsModule} from "./components/records-table-view-components.module";
import {TableBodyComponent} from "./components/records-table-view-components/table-body/table-body.component";
import {RecordsTableViewService} from "./services/records-table-view.service";
import {SelectedRowStateService} from "../../services/table-services/selected-row-state.service";
import {LoadingService} from "../../services/loading.service";
import {ModalStatesService} from "../../services/table-services/modal-states.service";
import {WrapperElementSizeStateService} from "../../services/table-services/wrapper-element-size-state.service";
import {DEFAULT_ZOOM} from "../../constants/constants";
import {TableContextService} from "../../services/table-services/table-context.service";
import {CommonModule} from "@angular/common";

@Component({
  selector: 'app-records-table-view',
  imports: [
    RecordsTableViewComponentsModule,
    CommonModule
  ],
  templateUrl: './records-table-view.component.html',
  styleUrl: './records-table-view.component.scss',
  providers: [LoadingService, ColumnResizeService, TableContextService]
})
export class RecordsTableViewComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input({
    required: true,
    transform: reorderColumnsAccordingToOrderProperty
  }) tableColumnsParams: Array<RecordTableColumnParams> = [];
  @Input({ required: true }) tableActions: Array<RecordTableAction> = [];
  @Input({ required: true }) tableData: Pageable<RecordData> | null = null;
  @Input({ required: true }) tableTotals: Array<RecordTotalData> = [];
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Input() isNeedGroupsTable: boolean = true;
  @Output() sortParamsChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() recordUpdated: EventEmitter<RecordUpdateParams> = new EventEmitter<RecordUpdateParams>;
  @Output() recordDeleted: EventEmitter<string | number> = new EventEmitter<string | number>;
  @Output() reloadTableData: EventEmitter<void> = new EventEmitter<void>;
  @Output() newRecordSaved: EventEmitter<Partial<RecordData>> = new EventEmitter<Partial<RecordData>>;
  @ViewChild('tableBody') tableBody!: TableBodyComponent;

  private modalStatesService: ModalStatesService = inject(ModalStatesService);
  private wrapperElementSizeStateService: WrapperElementSizeStateService = inject(WrapperElementSizeStateService);
  private utilityService: UtilityService = inject(UtilityService);
  private elementRef: ElementRef = inject(ElementRef);
  private changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private resizeService: ResizeService = inject(ResizeService);
  private telegramNotificationsStateService: TelegramNotificationsStateService = inject(TelegramNotificationsStateService);
  private telegramNotificationsPollingService: TelegramNotificationsPollingService = inject(TelegramNotificationsPollingService);
  private injector: Injector = inject(Injector);
  private columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);
  private columnResizeService: ColumnResizeService = inject(ColumnResizeService);
  private recordsTableViewService: RecordsTableViewService = inject(RecordsTableViewService);
  private selectedRowStateService: SelectedRowStateService = inject(SelectedRowStateService);
  private loadingService: LoadingService = inject(LoadingService);
  private tableContextService: TableContextService = inject(TableContextService);

  public wrapperStyle: Record<string, string> = {};
  public isEditModeActive: boolean = false;
  public isAddNewRecordStateActive: boolean = false;
  public isNewRecordDataValid: boolean = false;
  public currentTableZoom: number = DEFAULT_ZOOM;
  public isAnyTableActionAvailable: boolean = false;
  public isResizeColumn: boolean = false;
  public currentSortOrder: SortOrder = 'none';
  public currentKeySortParam: string = '';
  public newRecordData: Partial<RecordData> = {};
  public isModalOpen: boolean = false;
  public isNeedGroups: boolean = true;
  private dataSubscriptions: Subscription = new Subscription();

  public ngOnInit(): void {
    this.columnResizeService.setTableId(this.resourceParams.path);
    this.tableContextService.setTableId(this.resourceParams.path);
    this.initResizeEffect();
    this.initModalEffects();
    this.wrapperStyle = this.recordsTableViewService.updateWrapperStyles(this.resourceParams.key, this.currentTableZoom);
  }

  public ngAfterViewInit(): void {
    if (this.modalStatesService.hasOpenModals()) {
      this.calculateZoomedTableSize();
      this.changeDetector.detectChanges();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['resourceParams']) {
      this.columnResizeService.setTableId(this.resourceParams.path);
      this.tableContextService.setTableId(this.resourceParams.path);
      changes['resourceParams']?.previousValue &&
        this.wrapperElementSizeStateService.resetSpecificWrapperElementSize(changes['resourceParams']?.previousValue.path);
    }
    this.setSortingColumnByUserPreferences();
    this.isAnyTableActionAvailable = this.getIsAnyTableActionAvailable();
    changes['tableColumnsParams'] && this.resetAndInitializeTableState();
  }

  public ngOnDestroy(): void {
    this.dataSubscriptions.unsubscribe();
    this.telegramNotificationsStateService.resetTelegramNotificationsState();
    this.telegramNotificationsPollingService.stopPolling();
    this.columnResizeService.clearAllRegisteredElements();
    this.selectedRowStateService.resetSelectedRow();
  }

  public shouldShowTable(): boolean {
    return !!this.wrapperElementSizeStateService.getWrapperElementSize(this.resourceParams.path)?.height;
  }

  public handleSortingChange(sortField: string): void {
    this.sortParamsChanged.emit(sortField);
  }

  public handleEditModeStateChange(isEditModeActive: boolean): void {
    this.isEditModeActive = isEditModeActive;
    this.isEditModeActive && this.resizeService.resetResizingState();
  }

  public handleAddRecordStateChange(isAddingNewRecord: boolean): void {
    this.isAddNewRecordStateActive = isAddingNewRecord;
    this.isAddNewRecordStateActive && this.resizeService.resetResizingState();
  }

  public handleSetNewRecordData(newRecordData: Partial<RecordData>): void {
    this.newRecordData = newRecordData;
    this.isNewRecordDataValid = this.recordsTableViewService.checkIsNewRecordDataValid(this.resourceParams.path, newRecordData);
  }

  public handleRecordUpdate(recordUpdateParams: RecordUpdateParams): void {
    this.recordUpdated.emit(recordUpdateParams);
  }

  public async handleRecordDelete(recordId: string | number): Promise<void> {
    this.recordDeleted.emit(recordId);
  }

  public handleReloadTableData(): void {
    this.reloadTableData.emit();
  }

  public handleZoomChange(zoomOption: number): void {
    this.currentTableZoom = zoomOption;
    this.calculateZoomedTableSize();
    this.utilityService.updateTooltipStyle('--current-table-zoom', `${this.currentTableZoom}%`);
    this.tableBody?.viewPort?.checkViewportSize();
    this.wrapperStyle = this.recordsTableViewService.updateWrapperStyles(this.resourceParams.path, this.currentTableZoom);
  }

  private initResizeEffect(): void {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.isResizeColumn = this.resizeService.isResizingSignal()[this.resourceParams.key];
      });
    });
  }

  private initModalEffects(): void {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.modalStatesService.hasOpenModals() !== this.isModalOpen) {
          this.isModalOpen = this.modalStatesService.hasOpenModals();
          this.isModalOpen && this.calculateZoomedTableSize();
        }
      });
    });
  }

  private calculateZoomedTableSize(): void {
    this.wrapperElementSizeStateService.setWrapperElementSizeState({
      element: this.elementRef.nativeElement,
      resourceParamsKey: this.resourceParams.path,
      currentTableZoom: this.currentTableZoom
    });
  }

  private resetAndInitializeTableState(): void {
    if (this.isModalOpen) { return; }

    this.loadingService.setIsLoadingState(true);
    this.columnResizeService.clearAllRegisteredElements();
    this.columnParamsStateService.setColumnParamsState(this.resourceParams.path, this.tableColumnsParams);
    this.isNeedGroups = this.isNeedGroupsTable ?
      this.columnParamsStateService.isNeedGroup(this.resourceParams.path) :
      false;
    this.recordsTableViewService.applyUserColumnPreferences({
      key: this.resourceParams.path,
      isModalOpen: this.modalStatesService.hasOpenModals()
    });
    this.selectedRowStateService.resetSelectedRow();
    this.loadingService.setIsLoadingState(false);
    this.changeDetector.detectChanges();
  }

  private getIsAnyTableActionAvailable(): boolean {
    return this.tableActions.some(action => availableTableActions.has(action));
  }

  private setSortingColumnByUserPreferences(): void {
    const { currentKeySortParam, currentSortOrder } = this.recordsTableViewService.setSortingColumnByUserPreferences({
      currentSortOrder: this.currentSortOrder,
      currentKeySortParam: this.currentKeySortParam
    });

    this.currentKeySortParam = currentKeySortParam;
    this.currentSortOrder = currentSortOrder;
  }
}
